import React, { useState, useEffect } from 'react';
import { Building2, Search, Plus, Edit, Trash2, Save, X, Calendar, Globe, MapPin, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Company } from '../types';
import { buildApiUrl, API_CONFIG } from '../config/api';

interface Region {
  id: number;
  name: string;
  country: string;
}

const CompanyManager: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [paginatedCompanies, setPaginatedCompanies] = useState<Company[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newCompany, setNewCompany] = useState<Partial<Company>>({
    name: '',
    code: '',
    region_id: 2,
    website: null,
    status: 'active',
    email: '',
    phone: '',
    description: '',
    address: '',
  });

  useEffect(() => {
    fetchCompanies();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCompanies(companies);
    } else {
      const filtered = companies.filter(company =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.region_name && company.region_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (company.website && company.website.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCompanies(filtered);
    }
  }, [companies, searchTerm]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedCompanies(filteredCompanies.slice(startIndex, endIndex));
  }, [filteredCompanies, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.COMPANIES));
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.REGIONS));
      const data = await response.json();
      setRegions(data.regions || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
      setRegions([]);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.COMPANIES), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCompany),
      });

      if (response.ok) {
        await fetchCompanies();
        setIsCreating(false);
        setNewCompany({
          name: '',
          code: '',
          region_id: 2,
          website: null,
          status: 'active',
        });
      }
    } catch (error) {
      console.error('Error creating company:', error);
    }
  };

  const handleUpdate = async (company: Company) => {
    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.COMPANIES}/${company.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(company),
      });

      if (response.ok) {
        await fetchCompanies();
        setEditingCompany(null);
      }
    } catch (error) {
      console.error('Error updating company:', error);
    }
  };

  const handleDelete = async (id: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this company? This will affect all related train lines.')) return;

    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.COMPANIES}/${id}`), {
        method: 'DELETE',
      });

      if (response.ok || response.status === 204) {
        await fetchCompanies();
      } else if (response.status === 400) {
        const errorData = await response.json();
        alert(`Error: ${errorData.detail}`);
      } else {
        console.error('Delete failed with status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        alert('Failed to delete company. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company. Please check your connection.');
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Building2 className="h-6 w-6 mr-2 text-admin-600" />
          Company Management
        </h2>
        <button
          onClick={() => setIsCreating(true)}
          className="admin-btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Company
        </button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="admin-card mb-6">
          <h3 className="text-lg font-semibold mb-4">Create New Company</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <input
              type="text"
              placeholder="Company Name"
              value={newCompany.name}
              onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
              className="admin-input"
            />
            <input
              type="text"
              placeholder="Company Code (e.g., BTS)"
              value={newCompany.code}
              onChange={(e) => setNewCompany({ ...newCompany, code: e.target.value })}
              className="admin-input"
            />
            <input
              type="text"
              placeholder="Website (optional)"
              value={newCompany.website || ''}
              onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value || null })}
              className="admin-input"
            />
            <select
              value={newCompany.region_id || ''}
              onChange={(e) => setNewCompany({ ...newCompany, region_id: parseInt(e.target.value) })}
              className="admin-input"
              required
            >
              <option value="">Select Region</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}, {region.country}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} className="btn-primary flex items-center">
              <Save className="h-4 w-4 mr-2" />
              Save
            </button>
            <button
              onClick={() => setIsCreating(false)}
              className="btn-secondary flex items-center"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="admin-card mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search companies by name, description, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-input pl-10"
          />
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {paginatedCompanies.map((company) => (
          <div key={company.id} className="admin-card">
            {editingCompany?.id === company.id ? (
              // Edit Mode
              <div>
                <h3 className="text-lg font-semibold mb-4">Edit Company</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editingCompany.name}
                    onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="Company Name"
                  />
                  <input
                    type="text"
                    value={editingCompany.code}
                    onChange={(e) => setEditingCompany({ ...editingCompany, code: e.target.value })}
                    className="admin-input text-sm"
                    placeholder="Company Code"
                  />
                  <input
                    type="text"
                    value={editingCompany.website || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, website: e.target.value || null })}
                    className="admin-input text-sm"
                    placeholder="Website"
                  />
                  <select
                    value={editingCompany.region_id || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, region_id: parseInt(e.target.value) })}
                    className="admin-input text-sm"
                    required
                  >
                    <option value="">Select Region</option>
                    {regions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}, {region.country}
                      </option>
                    ))}
                  </select>
                  <select
                    value={editingCompany.status}
                    onChange={(e) => setEditingCompany({ ...editingCompany, status: e.target.value })}
                    className="admin-input text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => handleUpdate(editingCompany)}
                    className="btn-primary flex items-center text-sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={() => setEditingCompany(null)}
                    className="btn-secondary flex items-center text-sm"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <Building2 className="h-8 w-8 text-admin-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                      <div className="flex items-center mt-1">
                        {company.status === 'active' ? (
                          <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500 mr-1" />
                        )}
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          company.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {company.status ? company.status.charAt(0).toUpperCase() + company.status.slice(1) : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingCompany(company)}
                      className="text-admin-600 hover:text-admin-900 p-1"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(company.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Code:</span>
                      {company.code}
                    </div>
                    {company.website && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Globe className="h-4 w-4 mr-2 text-gray-400" />
                        <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-blue-600 hover:underline">
                          {company.website}
                        </a>
                      </div>
                    )}
                    {company.region_name && (
                      <div className="flex items-start text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                        <span>{company.region_name}{company.region_country && `, ${company.region_country}`}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center text-xs text-gray-500 pt-2 border-t">
                    <Calendar className="h-4 w-4 mr-1" />
                    Created: {new Date(company.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {filteredCompanies.length > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200 mt-6 rounded-lg">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredCompanies.length)} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredCompanies.length)} of {filteredCompanies.length} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.ceil(filteredCompanies.length / itemsPerPage) }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === Math.ceil(filteredCompanies.length / itemsPerPage) ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-admin-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(filteredCompanies.length / itemsPerPage), currentPage + 1))}
              disabled={currentPage === Math.ceil(filteredCompanies.length / itemsPerPage)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {filteredCompanies.length === 0 && !loading && (
        <div className="admin-card text-center py-8">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No companies found.</p>
        </div>
      )}
    </div>
  );
};

export default CompanyManager;